import React, { useState, useEffect } from 'react';
import { Download, Smartphone, Apple, Chrome, CheckCircle, Wifi, Bell, Zap, Shield, Globe, Share, Plus, MoreVertical, X } from 'lucide-react';

const AppDownloadPage = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isChrome, setIsChrome] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [showAndroidModal, setShowAndroidModal] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsInstalled(standalone);

    // Detect platform and browser
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const android = /Android/i.test(userAgent);
    const safari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const chrome = /Chrome/i.test(userAgent) && !/Edge|Edg/i.test(userAgent);
    
    setIsIOS(iOS);
    setIsAndroid(android);
    setIsSafari(safari || iOS); // iOS always uses Safari engine
    setIsChrome(chrome);

    // Listen for install prompt (Chrome/Edge only)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    // If we have a deferred prompt (Chrome/Edge), use it
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setIsInstalled(true);
        }
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Install prompt error:', error);
        // Fall back to showing instructions
        if (isIOS) {
          setShowIOSModal(true);
        } else {
          setShowAndroidModal(true);
        }
      }
    } else if (isIOS) {
      // iOS - always show manual instructions
      setShowIOSModal(true);
    } else if (isAndroid) {
      // Android without prompt - show instructions
      setShowAndroidModal(true);
    } else {
      // Desktop without prompt - show Chrome instructions
      setShowAndroidModal(true);
    }
  };

  const features = [
    { icon: Zap, title: 'Lightning Fast', desc: 'Instant loading, even offline' },
    { icon: Bell, title: 'Push Notifications', desc: 'Stay updated on new content' },
    { icon: Wifi, title: 'Works Offline', desc: 'Access courses without internet' },
    { icon: Shield, title: 'Secure', desc: 'Your data is always protected' },
    { icon: Globe, title: 'Always Updated', desc: 'Latest features automatically' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black pt-20">
      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Content */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-purple-500/20 px-4 py-2 rounded-full mb-6">
              <Smartphone className="w-4 h-4 text-purple-400" />
              <span className="text-purple-300 text-sm font-medium">Available on all devices</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Get the
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text"> OA Academy </span>
              App
            </h1>
            
            <p className="text-gray-300 text-lg mb-8 max-w-lg mx-auto lg:mx-0">
              Install our app for the best experience. Access your courses, community, and resources directly from your home screen.
            </p>

            {isInstalled ? (
              <div className="inline-flex items-center gap-3 bg-green-500/20 text-green-400 px-6 py-4 rounded-2xl">
                <CheckCircle className="w-6 h-6" />
                <span className="font-semibold">App Already Installed!</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                {/* Main Install Button */}
                <button
                  onClick={handleInstall}
                  className="group bg-white text-purple-900 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-purple-100 transition-all transform hover:scale-105 flex items-center justify-center gap-3 shadow-xl shadow-purple-500/25"
                >
                  <Download className="w-6 h-6 group-hover:animate-bounce" />
                  {isIOS ? 'How to Install' : 'Install Now - Free'}
                </button>
              </div>
            )}

            {/* Platform info */}
            <div className="mt-6 text-sm text-gray-400">
              {isIOS ? (
                <p className="flex items-center justify-center lg:justify-start gap-2">
                  <Apple className="w-4 h-4" />
                  Tap the button above for iOS installation steps
                </p>
              ) : isAndroid ? (
                <p className="flex items-center justify-center lg:justify-start gap-2">
                  <Chrome className="w-4 h-4" />
                  {deferredPrompt ? 'Click to install directly' : 'Tap for installation steps'}
                </p>
              ) : (
                <p>Works on iOS, Android, and Desktop browsers</p>
              )}
            </div>

            {/* Platform badges */}
            <div className="flex items-center justify-center lg:justify-start gap-6 mt-8">
              <div className={`flex items-center gap-2 ${isIOS ? 'text-purple-400' : 'text-gray-400'}`}>
                <Apple className="w-5 h-5" />
                <span className="text-sm">iOS</span>
              </div>
              <div className={`flex items-center gap-2 ${isAndroid ? 'text-purple-400' : 'text-gray-400'}`}>
                <Smartphone className="w-5 h-5" />
                <span className="text-sm">Android</span>
              </div>
              <div className={`flex items-center gap-2 ${!isIOS && !isAndroid ? 'text-purple-400' : 'text-gray-400'}`}>
                <Globe className="w-5 h-5" />
                <span className="text-sm">Desktop</span>
              </div>
            </div>
          </div>

          {/* Right - Phone Mockup */}
          <div className="relative flex justify-center">
            <div className="relative">
              {/* Phone frame */}
              <div className="w-64 sm:w-72 h-[500px] sm:h-[580px] bg-gray-800 rounded-[3rem] p-3 shadow-2xl shadow-purple-500/30 border border-gray-700">
                {/* Screen */}
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-purple-900 rounded-[2.5rem] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-gray-800 rounded-b-2xl"></div>
                  
                  {/* App content preview */}
                  <div className="pt-12 px-4">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-white/20 rounded-2xl mx-auto mb-3 flex items-center justify-center">
                        <span className="text-white font-bold text-xl">OA</span>
                      </div>
                      <h3 className="text-white font-semibold">OA Academy</h3>
                    </div>
                    
                    {/* Mini dashboard preview */}
                    <div className="space-y-3">
                      <div className="bg-white/10 rounded-xl p-3">
                        <div className="h-2 bg-white/30 rounded w-3/4 mb-2"></div>
                        <div className="h-2 bg-white/20 rounded w-1/2"></div>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3">
                        <div className="h-2 bg-white/30 rounded w-2/3 mb-2"></div>
                        <div className="h-2 bg-white/20 rounded w-1/3"></div>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3">
                        <div className="h-2 bg-white/30 rounded w-4/5 mb-2"></div>
                        <div className="h-2 bg-white/20 rounded w-2/5"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bottom nav */}
                  <div className="absolute bottom-4 left-4 right-4 bg-white/10 rounded-2xl p-3 flex justify-around">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-8 h-8 bg-white/20 rounded-lg"></div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/30 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-pink-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
          Why Install Our App?
        </h2>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
            >
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-white font-semibold text-lg mb-2">{feature.title}</h3>
              <p className="text-gray-400">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* iOS Installation Instructions - Always visible on iOS */}
      {isIOS && !isInstalled && (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur-sm border border-purple-500/30 rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Apple className="w-8 h-8 text-white" />
              <h3 className="text-2xl font-bold text-white">Install on iPhone/iPad</h3>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  1
                </div>
                <div>
                  <p className="text-white font-medium text-lg">Tap the Share button</p>
                  <p className="text-gray-300">Look for the <span className="inline-flex items-center gap-1 bg-blue-500/20 px-2 py-1 rounded"><Share className="w-4 h-4" /></span> icon at the bottom of Safari</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  2
                </div>
                <div>
                  <p className="text-white font-medium text-lg">Scroll and tap "Add to Home Screen"</p>
                  <p className="text-gray-300">Look for <span className="inline-flex items-center gap-1 bg-gray-700 px-2 py-1 rounded"><Plus className="w-4 h-4" /> Add to Home Screen</span></p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  3
                </div>
                <div>
                  <p className="text-white font-medium text-lg">Tap "Add" in the top right</p>
                  <p className="text-gray-300">The app will appear on your home screen!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Android/Desktop Instructions - Always visible when no prompt available */}
      {!isIOS && !deferredPrompt && !isInstalled && (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 backdrop-blur-sm border border-purple-500/30 rounded-3xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <Chrome className="w-8 h-8 text-white" />
              <h3 className="text-2xl font-bold text-white">
                {isAndroid ? 'Install on Android' : 'Install on Desktop'}
              </h3>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  1
                </div>
                <div>
                  <p className="text-white font-medium text-lg">Open in Chrome browser</p>
                  <p className="text-gray-300">Make sure you're using Google Chrome for best experience</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  2
                </div>
                <div>
                  <p className="text-white font-medium text-lg">Tap the menu</p>
                  <p className="text-gray-300">
                    {isAndroid 
                      ? <>Look for <span className="inline-flex items-center gap-1 bg-gray-700 px-2 py-1 rounded"><MoreVertical className="w-4 h-4" /></span> in the top right</>
                      : <>Look for the install icon in the address bar, or use Menu → "Install OA Academy"</>
                    }
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
                  3
                </div>
                <div>
                  <p className="text-white font-medium text-lg">
                    {isAndroid ? 'Tap "Add to Home screen"' : 'Click "Install"'}
                  </p>
                  <p className="text-gray-300">The app will be installed and ready to use!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iOS Modal */}
      {showIOSModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 p-6 text-white relative">
              <button 
                onClick={() => setShowIOSModal(false)}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <Apple className="w-10 h-10" />
                <div>
                  <h3 className="text-xl font-bold">Install on iPhone/iPad</h3>
                  <p className="text-purple-200 text-sm">Follow these simple steps</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Share className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">1. Tap Share button</p>
                  <p className="text-gray-500 text-sm">At the bottom of Safari</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Plus className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">2. Add to Home Screen</p>
                  <p className="text-gray-500 text-sm">Scroll down to find this option</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">3. Tap "Add"</p>
                  <p className="text-gray-500 text-sm">Done! Find the app on your home screen</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowIOSModal(false)}
                className="w-full bg-purple-600 text-white py-4 rounded-2xl font-semibold hover:bg-purple-700 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Android/Desktop Modal */}
      {showAndroidModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden animate-scale-in">
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white relative">
              <button 
                onClick={() => setShowAndroidModal(false)}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <Chrome className="w-10 h-10" />
                <div>
                  <h3 className="text-xl font-bold">Install via Chrome</h3>
                  <p className="text-blue-200 text-sm">Follow these simple steps</p>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <MoreVertical className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">1. Open Chrome menu</p>
                  <p className="text-gray-500 text-sm">Tap ⋮ in the top right corner</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">2. "Install app" or "Add to Home"</p>
                  <p className="text-gray-500 text-sm">Look for install option in menu</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">3. Confirm installation</p>
                  <p className="text-gray-500 text-sm">The app will appear on your device!</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowAndroidModal(false)}
                className="w-full bg-blue-600 text-white py-4 rounded-2xl font-semibold hover:bg-blue-700 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-bold text-white mb-4">
          Ready to Transform Your Journey?
        </h2>
        <p className="text-gray-400 mb-8">
          Join thousands of students growing spiritually with Obed Ansu Academy
        </p>
        
        {!isInstalled && (
          <button
            onClick={handleInstall}
            className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-opacity inline-flex items-center gap-3"
          >
            <Download className="w-6 h-6" />
            {isIOS ? 'See Installation Steps' : 'Install Free App'}
          </button>
        )}
      </div>

      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AppDownloadPage;
